import { getArea, getLength } from 'ol/sphere.js';
import Overlay from 'ol/Overlay.js';

import { formatArea, formatLength } from '@utils/measurementSystem';

/**
 * Formats the area of a geometry feature as a human-readable string.
 * Uses the OpenLayers getArea function to calculate the area of the geometry,
 * then formats it using the measurement system set in the application.
 *
 * @param {import('ol/geom').Geometry} geometry - The OpenLayers geometry object to measure
 * @returns {string} Formatted string showing the area with appropriate units (e.g., "Area: 10.50 m²")
 */
const getAreaString = geometry => {
	const [area, unit] = formatArea(getArea(geometry));
	return `Area: ${area} ${unit}`;
};

/**
 * Formats the length of a geometry feature as a human-readable string.
 * Uses the OpenLayers getLength function to calculate the length of the geometry,
 * then formats it using the measurement system set in the application.
 *
 * @param {import('ol/geom').Geometry} geometry - The OpenLayers geometry object to measure
 * @returns {string} Formatted string showing the length with appropriate units (e.g., "Length: 10.50 m")
 */
const getLengthString = geometry => {
	const [length, unit] = formatLength(getLength(geometry));
	return `Length: ${length} ${unit}`;
};

/**
 * Creates a tooltip element with the specified class name and style.
 *
 * @param {Object} options - The options for creating the tooltip element.
 * @param {string} options.className - The class name for the tooltip element.
 * @param {string} options.style - The style for the tooltip element.
 * @returns {HTMLElement} The created tooltip element.
 */
const createTooltipElement = ({ className, style }) => {
	const element = document.createElement('div');
	element.className = className;
	element.style = style;
	return element;
};

/**
 * Creates an overlay for a map object.
 * @param {Object} options The options for creating the overlay.
 * @param {MapObject} options.mapObject The map object to add the overlay to.
 * @param {String} options.type The type of the feature to overlay.
 * @param {String} options.className The class name for the overlay element.
 * @returns {Overlay} The created tooltip overlay.
 */
export const createOverlay = ({ mapObject, type, className }) => {
	const tooltipOverlay = new Overlay({
		element: createTooltipElement({
			className: className,
			style: `font-size: 12px; margin-left: 3px;`, // 3px to fix tooltip alignment.
		}),
		positioning: 'bottom-center',
		offset: type === 'LineString' ? [0, -7.5] : [0, 0],
	});

	tooltipOverlay.getElement().parentElement.style.pointerEvents = 'none';
	mapObject.addOverlay(tooltipOverlay);

	return tooltipOverlay;
};

/**
 * Removes an overlay from the map.
 *
 * @param {Object} options - The options for removing the overlay.
 * @param {Object} options.mapObject - The map object.
 * @param {Object} options.overlay - The overlay to be removed.
 */
export const removeOverlay = ({ mapObject, overlay }) => {
	overlay.getElement().remove();
	mapObject.removeOverlay(overlay);
};

/**
 * Retrieves overlay information based on the type and geometry.
 * @param {Object} options The options object.
 * @param {String} options.type The type of the feature to overlay.
 * @param {Object} options.geometry The geometry of the feature to overlay.
 * @returns {Array} [String, Coordinate].
 */
const getOverlayInfo = ({ type, geometry }) => {
	if (type === 'Polygon')
		return [
			getAreaString(geometry),
			geometry.getInteriorPoint().getCoordinates(),
		];

	if (type === 'LineString')
		return [getLengthString(geometry), geometry.getLastCoordinate()];
};

/**
 * Sets the overlay information.
 *
 * @param {Object} options The options for setting the overlay information.
 * @param {Overlay} options.overlay The overlay.
 * @param {string} options.type The type of the feature to overlay.
 * @param {Object} options.geometry The geometry of the feature to overlay.
 * @returns {void}
 */
export const setOverlayInfo = ({ overlay, type, geometry }) => {
	const [text, coordinate] = getOverlayInfo({
		type: type,
		geometry: geometry,
	});

	overlay.getElement().innerText = text;
	overlay.setPosition(coordinate);
};

/**
 * Listens for a specific keyboard key press event and executes a callback function once.
 *
 * @param {Object} options The options for the keyboard listener.
 * @param {string} options.key The key to listen for.
 * @param {Function} options.callback The callback function to execute when the key is pressed.
 */
export const keyboardListenOnce = ({ key, callback }) => {
	document.addEventListener(
		'keydown',
		e => {
			if (e.key.toLowerCase() === key.toLowerCase()) callback();
		},
		{ once: true }
	);
};
